// ProductModal.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Form, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import { FiZap, FiInfo, FiShoppingCart, FiTrendingUp, FiPackage, FiPlus } from 'react-icons/fi';
import { useRechercherCompteMutation, useSuggestionsIntelligentesMutation } from 'actions/rag_compte/mutations';
import { useCreateCompteComptableMutation } from 'actions/plan_comptable/mutations';
import { useGetCodesQuery } from 'actions/comptabilite/queries';

// ─── Configuration par nature comptable ────────────────────────────────────────
const NATURE_CONFIG = {
  vente: {
    label: 'Vente / Produit',
    icon: <FiTrendingUp />,
    color: '#068f7a',
    classesPrioritaires: ['7'],
    hint: 'Revenus générés par l\'activité (classe 7)',
    exemples: ['701 - Ventes de marchandises', '706 - Prestations de services'],
  },
  achat: {
    label: 'Achat / Charge',
    icon: <FiShoppingCart />,
    color: '#e67e22',
    classesPrioritaires: ['6'],
    hint: 'Charges consommées par l\'activité (classe 6)',
    exemples: ['601 - Achat de marchandises', '604 - Achat d\'études et prestations'],
  },
  immobilisation: {
    label: 'Immobilisation',
    icon: <FiPackage />,
    color: '#8e44ad',
    classesPrioritaires: ['2'],
    hint: 'Bien utilisé durablement dans l\'entreprise (classe 2)',
    exemples: ['2183 - Matériel informatique', '215 - Matériel et outillage'],
  },
};

// Déduit la nature par défaut depuis le prop type du parent
const getNatureFromType = (type) => {
  if (type === 'vente') return 'vente';
  if (type === 'depense') return 'achat';
  return null; // laisse l'utilisateur choisir
};

function ProductModal({
  showModal,
  handleClose,
  formMethods,
  creationProduit,
  dossier_id,
  type, // "vente" | "depense"
  onProductCreated,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
  } = formMethods;

  // ─── Récupération du plan comptable complet (SYSCOHADA + Personnalisés) ───
  const { data: rawCodesData, isLoading: isLoadingPlan, refetch: refetchPlan } = useGetCodesQuery(
    { dossier_client: dossier_id },
    { skip: !dossier_id }
  );

  // Normaliser les données (peut être array ou {data: array})
  const planComptable = useMemo(() => {
    if (!rawCodesData) return [];
    if (Array.isArray(rawCodesData)) return rawCodesData;
    if (rawCodesData.data && Array.isArray(rawCodesData.data)) return rawCodesData.data;
    if (rawCodesData.results && Array.isArray(rawCodesData.results)) return rawCodesData.results;
    return [];
  }, [rawCodesData]);

  const [createCompteComptable, { isLoading: isCreatingCompte }] = useCreateCompteComptableMutation();

  const [rechercherCompte, { isLoading: isLoadingRecherche }] = useRechercherCompteMutation();
  const [suggestionsIntelligentes, { isLoading: isLoadingSuggestions }] = useSuggestionsIntelligentesMutation();

  // ─── Nature comptable ────────────────────────────────────────────────────────
  const defaultNature = getNatureFromType(type);
  const [natureSelectionnee, setNatureSelectionnee] = useState(defaultNature);
  const natureConfig = natureSelectionnee ? NATURE_CONFIG[natureSelectionnee] : null;

  const [errorCompte, setErrorCompt] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCompteData, setNewCompteData] = useState({ numero: '', libelle: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const watchCodeCompte = watch('code_comptable', null);
  const watchDescription = watch('description', '');
  const watchNom = watch('nom', '');

  const debounceTimer = useRef(null);
  const lastSearchedDescription = useRef('');
  const lastAutoFilledNom = useRef('');

  // ─── Soumission ──────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    const codeComptable = data.code_comptable?.value || data.code_comptable;
    setIsSubmitting(true);
    creationProduit({ ...data, code_comptable: codeComptable, dossier_id })
      .unwrap()
      .then((createdProduct) => {
        setIsSubmitting(false);
        reset();
        setSuggestions([]);
        setShowSuggestions(false);
        toast.success('Création de produit réussie !');
        onProductCreated?.(createdProduct);
        handleClose();
      })
      .catch((error) => {
        setIsSubmitting(false);
        toast.error('Une erreur est survenue');
        if (error?.data?.message) toast.error(`Erreur: ${error.data.message}`);
      });
  };

  // ─── Options du select filtrées par nature ───────────────────────────────────
  const productAccounts = useMemo(() => {
    if (showSuggestions && suggestions.length > 0) {
      return suggestions.map((compte) => ({
        value: compte.code.toString(),
        label: `${compte.code} - ${compte.libelle}`,
        score: compte.score_confiance,
        isAISuggestion: true,
      }));
    }

    const uniques = new Map();

    // Utiliser le plan comptable combiné (SYSCOHADA + Personnalisés)
    if (Array.isArray(planComptable)) {
      planComptable.forEach((compte) => {
        const codeStr = (compte?.code ?? compte?.numero_compte ?? '').toString();
        if (!codeStr) return;
        
        const firstDigit = parseInt(codeStr.charAt(0));
        if (isNaN(firstDigit) || firstDigit < 1 || firstDigit > 7) return;
        
        const libelle = compte?.libelle || compte?.combine || 'Compte';
        
        uniques.set(codeStr, {
          value: codeStr,
          label: `${codeStr} - ${libelle}`,
          isCustom: compte?.is_custom || false,
        });
      });
    }

    let all = Array.from(uniques.values()).sort((a, b) => {
      // Tri: personnalisés en premier, puis par code
      if (a.isCustom && !b.isCustom) return -1;
      if (!a.isCustom && b.isCustom) return 1;
      return a.value.localeCompare(b.value, undefined, { numeric: true });
    });

    // Filtre selon la nature sélectionnée
    if (natureConfig?.classesPrioritaires) {
      const prioritaires = all.filter((opt) =>
        natureConfig.classesPrioritaires.some((c) => opt.value.startsWith(c))
      );
      const autres = all.filter(
        (opt) => !natureConfig.classesPrioritaires.some((c) => opt.value.startsWith(c))
      );
      return [...prioritaires, ...autres];
    }

    return all;
  }, [planComptable, suggestions, showSuggestions, natureConfig]);

  // ─── Recherche IA ────────────────────────────────────────────────────────────
  const performSearch = useCallback(
    async (searchText) => {
      if (!searchText || searchText.trim().length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      if (searchText === lastSearchedDescription.current) return;
      lastSearchedDescription.current = searchText;

      // Enrichir la requête avec la nature comptable pour guider l'IA
      const contexte = natureSelectionnee
        ? `[${NATURE_CONFIG[natureSelectionnee].label}] ${searchText}`
        : searchText;

      try {
        const res = await suggestionsIntelligentes({
          description: contexte,
          dossier_id,
          top_k: 3,
        }).unwrap();

        if (res?.suggestions?.length > 0) {
          setSuggestions(res.suggestions);
          setShowSuggestions(true);
          return;
        }
      } catch (e) {console.error('Erreur lors de la recherche de suggestions intelligentes:', e);}

      try {
        const res = await rechercherCompte({
          description: contexte,
          top_k: 5,
          seuil_confiance: 0.1,
        }).unwrap();

        if (res?.comptes?.length > 0) {
          setSuggestions(res.comptes);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [rechercherCompte, suggestionsIntelligentes, dossier_id, natureSelectionnee]
  );

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const searchText = `${watchNom} ${watchDescription}`.trim();
    if (searchText.length >= 3) {
      debounceTimer.current = setTimeout(() => performSearch(searchText), 500);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    return () => clearTimeout(debounceTimer.current);
  }, [watchNom, watchDescription, performSearch]);

  useEffect(() => {
    setErrorCompt('');
  }, [watchCodeCompte]);

  useEffect(() => {
    if (!showModal) {
      setSuggestions([]);
      setShowSuggestions(false);
      lastSearchedDescription.current = '';
      setNatureSelectionnee(defaultNature);
    }
  }, [showModal, defaultNature]);

  const isLoadingAI = isLoadingRecherche || isLoadingSuggestions;
  const accentColor = natureConfig?.color || '#068f7a';

  // ─── Création d'un compte à la volée ────────────────────────────────────────
  const handleCreateCompte = async (inputValue) => {
    // Extraire numéro et libellé si format "123 - Libellé"
    const match = inputValue.match(/^(\d+)\s*-?\s*(.*)$/);
    const numero_compte = match ? match[1] : inputValue;
    const libelle = match && match[2] ? match[2].trim() : '';

    // Si pas de libellé, ouvrir modal pour le saisir
    if (!libelle) {
      setNewCompteData({ numero: numero_compte, libelle: '' });
      setShowCreateModal(true);
      return null;
    }

    const newOption = await createCompteWithData(numero_compte, libelle);
    if (newOption) {
      const currentNom = formMethods.getValues ? formMethods.getValues('nom') : watchNom;
      if (!currentNom || !currentNom.trim() || currentNom === lastAutoFilledNom.current) {
        formMethods.setValue('nom', libelle.trim());
        lastAutoFilledNom.current = libelle.trim();
      }
    }
    return newOption;
  };

  const createCompteWithData = async (numero_compte, libelle) => {
    try {
      await createCompteComptable({
        numero_compte,
        libelle,
        dossier_id,
      }).unwrap();

      toast.success(`Compte ${numero_compte} créé avec succès!`);
      
      // Rafraîchir le plan comptable
      refetchPlan();
      
      // Retourner l'option pour le select
      return {
        value: numero_compte,
        label: `${numero_compte} - ${libelle}`,
        libelle: libelle,
        isCustom: true,
      };
    } catch (error) {
      console.error('Erreur création compte:', error);
      // Afficher l'erreur exacte retournée par le backend
      const errorMessage = error?.data?.error || error?.data?.message || error?.message || 'Erreur lors de la création du compte';
      toast.error(errorMessage);
      return null;
    }
  };

  const handleConfirmCreateCompte = async () => {
    if (!newCompteData.libelle.trim()) {
      toast.error('Le libellé est obligatoire');
      return;
    }

    const newOption = await createCompteWithData(newCompteData.numero, newCompteData.libelle);
    if (newOption) {
      // Sélectionner automatiquement le compte créé
      formMethods.setValue('code_comptable', newOption);
      const currentNom = formMethods.getValues ? formMethods.getValues('nom') : watchNom;
      if (!currentNom || !currentNom.trim() || currentNom === lastAutoFilledNom.current) {
        formMethods.setValue('nom', newCompteData.libelle.trim());
        lastAutoFilledNom.current = newCompteData.libelle.trim();
      }
      setShowCreateModal(false);
      setNewCompteData({ numero: '', libelle: '' });
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Ajouter un produit/service</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>

          {/* ── Nom ── */}
          <Form.Group controlId="formNom" className="form-group mb-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Form.Text>Nom</Form.Text>
            <Form.Control
              {...register('nom', { required: 'Ce champ est requis' })}
              className="form-controll"
              placeholder="Nom du produit"
            />
            {errors.nom && <div className="text-danger">{errors.nom.message}</div>}
          </Form.Group>

          {/* ── Nature comptable ── */}
          <Form.Group className="mb-3">
            <Form.Text className="d-block mb-2" style={{ fontWeight: 600 }}>
              Nature comptable
            </Form.Text>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(NATURE_CONFIG).map(([key, cfg]) => {
                const isSelected = natureSelectionnee === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setNatureSelectionnee(key);
                      // Réinitialiser suggestions pour relancer avec nouveau contexte
                      setSuggestions([]);
                      setShowSuggestions(false);
                      lastSearchedDescription.current = '';
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      border: `2px solid ${isSelected ? cfg.color : '#dee2e6'}`,
                      backgroundColor: isSelected ? cfg.color : 'white',
                      color: isSelected ? 'white' : '#6c757d',
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {cfg.icon}
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Info bulle contextuelle */}
            {natureConfig && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  backgroundColor: `${accentColor}12`,
                  borderLeft: `3px solid ${accentColor}`,
                  fontSize: '0.82rem',
                  color: '#444',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px', color: accentColor }}>
                  {natureConfig.icon} {natureConfig.hint}
                </div>
                <div style={{ color: '#666' }}>
                  Exemples : {natureConfig.exemples.join(' · ')}
                </div>
              </div>
            )}
          </Form.Group>

          {/* ── Compte comptable ── */}
          <Form.Group controlId="formCodeComptable" className="form-group mb-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Form.Text className="form-label">
              Type
              {isLoadingAI && <Spinner animation="border" size="sm" className="ms-2" />}
              {showSuggestions && suggestions.length > 0 && (
                <Badge bg="success" className="ms-2" style={{ fontSize: '0.75rem' }}>
                  <FiZap style={{ marginRight: '4px' }} />
                  {suggestions.length} suggestions IA
                </Badge>
              )}
            </Form.Text>
            <div style={{ width: '100%' }}>
              <Controller
                control={control}
                name="code_comptable"
                rules={{ required: 'Veuillez sélectionner un compte comptable' }}
                render={({ field }) => (
                  <CreatableSelect
                    {...field}
                    value={field.value || null}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      if (newValue) {
                        setShowSuggestions(false);
                        setSuggestions([]);
                        // Dès que le numéro de compte est sélectionné, pré-remplir le nom/libellé du produit
                        const rawLabel = newValue.label || '';
                        const extractedLabel = rawLabel.includes(' - ')
                          ? rawLabel.split(' - ').slice(1).join(' - ').trim()
                          : (newValue.libelle || rawLabel);
                        const currentNom = formMethods.getValues ? formMethods.getValues('nom') : watchNom;
                        if (extractedLabel && (!currentNom || !currentNom.trim() || currentNom === lastAutoFilledNom.current)) {
                          formMethods.setValue('nom', extractedLabel);
                          lastAutoFilledNom.current = extractedLabel;
                        }
                      }
                    }}
                    options={productAccounts}
                    placeholder={
                      natureConfig
                        ? `Comptes classe ${natureConfig.classesPrioritaires.join('/')} suggérés en premier…`
                        : 'Compte'
                    }
                    isClearable
                    isLoading={isLoadingPlan || isCreatingCompte}
                    isDisabled={isLoadingPlan || isCreatingCompte}
                    formatCreateLabel={(v) => (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiPlus style={{ color: accentColor }} />
                        Créer le compte &ldquo;{v}&rdquo;
                      </span>
                    )}
                    onCreateOption={async (inputValue) => {
                      const newOption = await handleCreateCompte(inputValue);
                      if (newOption) {
                        field.onChange(newOption);
                        const rawLabel = newOption.label || '';
                        const extractedLabel = rawLabel.includes(' - ')
                          ? rawLabel.split(' - ').slice(1).join(' - ').trim()
                          : (newOption.libelle || rawLabel);
                        const currentNom = formMethods.getValues ? formMethods.getValues('nom') : watchNom;
                        if (extractedLabel && (!currentNom || !currentNom.trim() || currentNom === lastAutoFilledNom.current)) {
                          formMethods.setValue('nom', extractedLabel);
                          lastAutoFilledNom.current = extractedLabel;
                        }
                      }
                    }}
                    formatOptionLabel={(option) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{option.label}</span>
                        {option.isAISuggestion && option.score && (
                          <Badge bg="success" style={{ fontSize: '0.7rem', marginLeft: '8px' }}>
                            {Math.round(option.score * 100)}%
                          </Badge>
                        )}
                      </div>
                    )}
                    noOptionsMessage={() => 'Aucun compte disponible. Tapez pour créer.'}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        height: '38px',
                        minWidth: '100%',
                        borderColor: showSuggestions ? accentColor : base.borderColor,
                        boxShadow: showSuggestions ? `0 0 0 1px ${accentColor}` : base.boxShadow,
                      }),
                      valueContainer: (base) => ({ ...base, height: '38px', padding: '0 8px' }),
                      input: (base) => ({ ...base, margin: '0', padding: '0' }),
                      indicatorsContainer: (base) => ({ ...base, height: '38px' }),
                    }}
                  />
                )}
              />
            </div>
            {errors.code_comptable && (
              <div className="text-danger">{errors.code_comptable.message}</div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <Form.Text className="text-muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                <FiInfo className="me-1" />
                Suggestions basées sur votre description. Vous pouvez aussi chercher ou créer un autre compte.
              </Form.Text>
            )}
          </Form.Group>

          {/* ── Description ── */}
          <Form.Group controlId="formDescription" className="form-group mb-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Form.Text className="form-label">Description</Form.Text>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('description')}
              className="form-controll"
              placeholder="Description du produit (optionnel)"
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
              <FiInfo className="me-1" />
              Décrivez le produit pour obtenir des suggestions IA
            </Form.Text>
          </Form.Group>

          {errorCompte && (
            <div className="text-danger" style={{ marginBottom: '10px' }}>{errorCompte}</div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ backgroundColor: accentColor, borderColor: accentColor }}
              disabled={errorCompte !== '' || isSubmitting}
            >
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </Form>
      </Modal.Body>

      {/* ── Modal création compte avec libellé ── */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Créer un compte comptable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Numéro de compte</Form.Label>
            <Form.Control
              type="text"
              value={newCompteData.numero}
              onChange={(e) => setNewCompteData({ ...newCompteData, numero: e.target.value })}
              placeholder="Ex: 638"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Libellé <span style={{ color: '#DC2626' }}>*</span></Form.Label>
            <Form.Control
              type="text"
              value={newCompteData.libelle}
              onChange={(e) => setNewCompteData({ ...newCompteData, libelle: e.target.value })}
              placeholder="Ex: Autres charges externes"
              autoFocus
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.85rem' }}>
              Le compte sera ajouté au plan comptable comme compte personnalisé
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => setShowCreateModal(false)}
          >
            Annuler
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleConfirmCreateCompte}
            disabled={!newCompteData.libelle.trim() || isCreatingCompte}
            style={{ backgroundColor: accentColor, borderColor: accentColor }}
          >
            {isCreatingCompte ? 'Création...' : 'Créer'}
          </button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}

export default ProductModal;